import { motion } from 'framer-motion';

import { cn } from '@llura/ui';

export type IntroAuraVariant = 'pulse' | 'swirl' | 'cocoon';

interface IntroAuraProps {
  variant: IntroAuraVariant;
  size?: number;
  className?: string;
}

/**
 * Three ambient aura variants, all built from the same concentric-rings +
 * radial-gradient core pattern used elsewhere (your-space-view, afterglow).
 * Kept Props-driven so screens stay declarative.
 *
 *  - `pulse`  → body-rhythm aura (faster, plum/champagne) — used for "Das Prinzip"
 *  - `swirl`  → slow rotating rings (rose-gold gradient) — used for "Die Kunst der Hingabe"
 *  - `cocoon` → static, emerald-tinted breath (safety) — used for "Der Pakt"
 */
export function IntroAura({ variant, size = 180, className }: IntroAuraProps) {
  return (
    <div
      className={cn('relative mx-auto flex items-center justify-center', className)}
      style={{ width: size, height: size }}
    >
      {variant === 'pulse' && <PulseAura />}
      {variant === 'swirl' && <SwirlAura />}
      {variant === 'cocoon' && <CocoonAura />}
    </div>
  );
}

function PulseAura() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          aria-hidden
          className="absolute inset-0 rounded-full border border-secondary/40"
          animate={{
            scale: [1 - i * 0.08, 1.06 - i * 0.08, 1 - i * 0.08],
            opacity: [0.4, 0.7, 0.4],
          }}
          transition={{
            duration: 3.5 + i * 0.4,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: i * 0.25,
          }}
        />
      ))}
      <motion.div
        aria-hidden
        className="absolute inset-8 rounded-full bg-[radial-gradient(circle_at_50%_50%,hsl(var(--secondary)/0.55),hsl(var(--primary)/0.25)_60%,transparent_80%)] blur-md"
        animate={{ scale: [0.95, 1.1, 0.95], opacity: [0.6, 0.9, 0.6] }}
        transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        aria-hidden
        className="absolute inset-[42%] rounded-full bg-accent/75"
        animate={{ opacity: [0.55, 1, 0.55] }}
        transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
      />
    </>
  );
}

function SwirlAura() {
  return (
    <>
      {[0, 1, 2, 3].map((i) => (
        <motion.div
          key={i}
          aria-hidden
          className="absolute inset-0 rounded-full border border-primary/35"
          style={{ transformOrigin: 'center' }}
          animate={{
            rotate: 360,
            scale: [1 - i * 0.06, 1.04 - i * 0.06, 1 - i * 0.06],
          }}
          transition={{
            rotate: { duration: 6.5 + i * 0.8, repeat: Infinity, ease: 'linear' },
            scale: { duration: 5 + i * 0.5, repeat: Infinity, ease: 'easeInOut' },
            delay: i * 0.3,
          }}
        />
      ))}
      <motion.div
        aria-hidden
        className="absolute inset-10 rounded-full bg-[radial-gradient(circle_at_50%_50%,hsl(var(--secondary)/0.55),hsl(var(--primary)/0.30)_55%,transparent_85%)] blur-md"
        animate={{ scale: [0.9, 1.05, 0.9], opacity: [0.55, 0.85, 0.55] }}
        transition={{ duration: 4.2, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        aria-hidden
        className="absolute inset-[44%] rounded-full bg-accent/70"
        animate={{ opacity: [0.4, 0.9, 0.4] }}
        transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut' }}
      />
    </>
  );
}

function CocoonAura() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          aria-hidden
          className="absolute inset-0 rounded-full border border-emerald-500/30"
          animate={{
            scale: [1 - i * 0.07, 1.04 - i * 0.07, 1 - i * 0.07],
            opacity: [0.25, 0.5, 0.25],
          }}
          transition={{
            duration: 8 + i * 0.6,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: i * 0.5,
          }}
        />
      ))}
      <motion.div
        aria-hidden
        className="absolute inset-2 rounded-full bg-[radial-gradient(circle_at_50%_55%,rgba(52,211,153,0.45),rgba(16,185,129,0.20)_55%,transparent_85%)] blur-md"
        animate={{ scale: [0.95, 1.07, 0.95], opacity: [0.5, 0.8, 0.5] }}
        transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        aria-hidden
        className="absolute inset-[44%] rounded-full bg-emerald-300/70"
        animate={{ opacity: [0.55, 0.95, 0.55] }}
        transition={{ duration: 3.4, repeat: Infinity, ease: 'easeInOut' }}
      />
    </>
  );
}
